import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, IconButton, InputAdornment, MenuItem,
  Select, SelectChangeEvent, TextField, Tooltip, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { callService } from '../services/callService';
import { RawLogRow } from '../types/diagnostics';

/**
 * Il log tecnico riga per riga, come Vapi lo scrive.
 *
 * È la vista di ultima istanza: quando la conversazione non basta a capire
 * cosa è successo, qui c'è tutto ciò che il fornitore ha registrato. Le righe
 * mantengono la struttura originale — chi legge il grezzo deve vedere le
 * chiavi vere, non una nostra riscrittura — meno i campi rimossi lato server,
 * che restano visibili come marcatori espliciti invece di sparire in silenzio.
 */

const LEVELS = ['LOG', 'INFO', 'WARN', 'ERROR', 'FATAL'] as const;

/** Colori di stato, non una palette categorica: sono quattro e sono riservati. */
const LEVEL_TONE: Record<string, 'default' | 'info' | 'warning' | 'error'> = {
  LOG: 'default', INFO: 'info', WARN: 'warning', ERROR: 'error', FATAL: 'error',
};

const mono = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontVariantNumeric: 'tabular-nums' as const,
};

const stamp = (ms?: number) =>
  ms == null ? '—' : new Date(ms).toLocaleString('it-IT', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    second: '2-digit', fractionalSecondDigits: 3,
  } as Intl.DateTimeFormatOptions);

const categoryOf = (r: RawLogRow) => String((r.attributes as any)?.category ?? '—');

/** Anteprima degli attributi su una riga sola, come nel grezzo. */
const preview = (r: RawLogRow) => {
  try {
    return JSON.stringify(r.attributes ?? {});
  } catch {
    return '';
  }
};

function RowDetail({ row }: { row: RawLogRow }) {
  const [copied, setCopied] = useState(false);
  const json = useMemo(() => JSON.stringify(row, null, 2), [row]);

  const copy = () => {
    navigator.clipboard?.writeText(json).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 1600); },
      () => setCopied(false),
    );
  };

  return (
    // `minWidth: 0` e `overflow: hidden` non sono decorazione: senza, il <pre>
    // sotto si allarga al contenuto invece di scorrere, e trascina con sé la
    // riga, la tabella e l'intera pagina — misurato, 390px → 578px su telefono.
    <Box sx={(t) => ({
      bgcolor: alpha(t.palette.primary.main, 0.04), px: 2, py: 1.5,
      minWidth: 0, maxWidth: '100%', overflow: 'hidden',
    })}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Record completo
        </Typography>
        <Tooltip title={copied ? 'Copiato' : 'Copia il JSON'} arrow>
          <IconButton size="small" onClick={copy} aria-label="Copia il JSON del record">
            <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        component="pre"
        sx={{
          ...mono, m: 0, p: 1.5, borderRadius: 1.5, fontSize: '0.76rem', lineHeight: 1.6,
          bgcolor: 'background.paper', border: 1, borderColor: 'divider',
          maxHeight: 420, maxWidth: '100%', overflow: 'auto', whiteSpace: 'pre', tabSize: 2,
        }}
      >
        {json}
      </Box>
    </Box>
  );
}

export default function CallRawLogs({ callId }: { callId: string }) {
  const [rows, setRows] = useState<RawLogRow[] | null>(null);
  const [meta, setMeta] = useState<{ empty: boolean; truncated?: boolean; retention_days?: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [level, setLevel] = useState('tutti');
  const [category, setCategory] = useState('tutte');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    // Niente guard su useRef qui: in StrictMode l'effetto gira due volte, il
    // cleanup del primo passaggio mette `alive = false`, e un guard farebbe
    // saltare il secondo fetch — lo stato non verrebbe mai impostato e il
    // pannello resterebbe sullo scheletro per sempre. `alive` basta già a
    // scartare una risposta arrivata tardi, e `[callId]` a non rifare il
    // lavoro senza motivo.
    let alive = true;
    setLoading(true);
    setError(null);
    callService
      .getCallRawLogs(callId)
      .then((d) => {
        if (!alive) return;
        setRows(d.rows ?? []);
        setMeta({ empty: d.empty, truncated: d.truncated, retention_days: d.retention_days });
      })
      .catch((e) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [callId]);

  const categories = useMemo(() => {
    const seen = new Set((rows ?? []).map(categoryOf));
    return Array.from(seen).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (rows ?? []).filter((r) => {
      if (level !== 'tutti' && (r.severityText ?? '') !== level) return false;
      if (category !== 'tutte' && categoryOf(r) !== category) return false;
      if (!q) return true;
      return `${r.body ?? ''} ${preview(r)}`.toLowerCase().includes(q);
    });
  }, [rows, query, level, category]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4, justifyContent: 'center' }}>
        <CircularProgress size={20} />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Scarico il log tecnico…
        </Typography>
      </Box>
    );
  }

  if (error) return <Alert severity="error" variant="outlined">{error}</Alert>;

  if (!rows || meta?.empty) {
    return (
      <Alert severity="info" variant="outlined">
        Log tecnico non disponibile. Viene conservato per {meta?.retention_days ?? 15} giorni
        insieme agli altri artifact della chiamata.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
        <TextField
          size="small"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpenIndex(null); }}
          placeholder="Cerca nel log"
          sx={{ flexGrow: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
        <Select
          size="small" value={level} sx={{ minWidth: 128 }}
          onChange={(e: SelectChangeEvent) => { setLevel(e.target.value); setOpenIndex(null); }}
        >
          <MenuItem value="tutti">Livello</MenuItem>
          {LEVELS.filter((l) => (rows ?? []).some((r) => r.severityText === l)).map((l) => (
            <MenuItem key={l} value={l}>{l}</MenuItem>
          ))}
        </Select>
        <Select
          size="small" value={category} sx={{ minWidth: 150 }}
          onChange={(e: SelectChangeEvent) => { setCategory(e.target.value); setOpenIndex(null); }}
        >
          <MenuItem value="tutte">Categoria</MenuItem>
          {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </Select>
      </Box>

      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
        {filtered.length === rows.length
          ? `${rows.length} righe`
          : `${filtered.length} righe su ${rows.length}`}
        {' · i campi sensibili sono rimossi dal server e segnati nel record'}
      </Typography>

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        {/* Intestazione: colonne nominate, come in una tabella vera. Le righe
            sono bottoni perché si aprono, quindi non è un <table> ma una lista. */}
        <Box
          sx={{
            display: { xs: 'none', md: 'grid' },
            gridTemplateColumns: '186px 84px 118px minmax(0, 1fr)',
            gap: 1.5, px: 2, py: 1, bgcolor: 'background.default',
            borderBottom: 1, borderColor: 'divider',
          }}
        >
          {['Ora', 'Livello', 'Categoria', 'Record'].map((h) => (
            <Typography key={h} variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {h}
            </Typography>
          ))}
        </Box>

        {filtered.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', p: 2 }}>
            Nessuna riga con questi filtri.
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 560, overflowY: 'auto' }}>
            {filtered.map((r, i) => {
              const open = openIndex === i;
              const lvl = r.severityText ?? '—';
              return (
                <Box key={i}>
                  <Box
                    component="button"
                    onClick={() => setOpenIndex(open ? null : i)}
                    aria-expanded={open}
                    sx={{
                      display: 'grid', width: '100%', textAlign: 'left', font: 'inherit',
                      // Stesso motivo della griglia di pagina: `1fr` non
                      // scende sotto il max-content della riga.
                      gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '186px 84px 118px minmax(0, 1fr)' },
                      gap: { xs: 0.5, md: 1.5 }, alignItems: 'baseline',
                      px: 2, py: 1.1, border: 0, borderTop: i ? 1 : 0, borderColor: 'divider',
                      cursor: 'pointer', bgcolor: open ? 'action.selected' : 'transparent',
                      '&:hover': { bgcolor: open ? 'action.selected' : 'action.hover' },
                      '&:focus-visible': {
                        outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2,
                      },
                    }}
                  >
                    <Typography variant="caption" sx={{ ...mono, color: 'text.secondary' }}>
                      {stamp(r.time)}
                    </Typography>
                    <Box>
                      <Chip
                        size="small" label={lvl} color={LEVEL_TONE[lvl] ?? 'default'}
                        variant={lvl === 'LOG' ? 'outlined' : 'filled'}
                        sx={{ height: 19, fontSize: '0.66rem', fontWeight: 600 }}
                      />
                    </Box>
                    {/* Categoria neutra: con dodici valori nessuna palette
                        categorica reggerebbe le soglie di separazione, e il
                        filtro fa il lavoro di scansione meglio del colore. */}
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {categoryOf(r)}
                    </Typography>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ ...mono, fontSize: '0.8rem' }}>
                        {r.body || '—'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          ...mono, color: 'text.disabled', display: 'block',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {preview(r)}
                      </Typography>
                    </Box>
                  </Box>
                  {open && <RowDetail row={r} />}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

    </Box>
  );
}
